import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import { FC, useEffect, useState } from 'react'
import { MdHistory } from 'react-icons/md'

import followUpFornecedorService from '@service/followUpFornecedor'

import LayoutToolsButton from '@component/Layout/Tools/Actions/Components/Button'

interface IHistoricoItem {
  id: number
  configId: number
  supplierId: number
  storeId: number
  acao: 'CRIADO' | 'ATUALIZADO' | 'REMOVIDO'
  emailAntes: string | null
  emailDepois: string | null
  usuarioLogin: string
  ocorridoEm: string
}

interface IPagination {
  totalRows: number
  rows: number
  actualPage: number
  totalPages: number
}

const TAKE = 50

const acaoBadge = (acao: string) => {
  if (acao === 'CRIADO') return <Badge colorScheme="green">{acao}</Badge>
  if (acao === 'REMOVIDO') return <Badge colorScheme="red">{acao}</Badge>
  return <Badge colorScheme="blue">{acao}</Badge>
}

const ModalHistoricoConfigFollowUp: FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<IHistoricoItem[]>([])
  const [pagination, setPagination] = useState<IPagination | null>(null)
  const [skip, setSkip] = useState(0)

  const fetchData = async (newSkip: number) => {
    setLoading(true)
    try {
      const { data: res } = await followUpFornecedorService.getHistoricoConfig({
        take: TAKE,
        skip: newSkip
      })
      setItems(res.data ?? [])
      setPagination(res.pagination ?? null)
      setSkip(newSkip)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchData(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleClose = () => setIsOpen(false)

  return (
    <>
      <LayoutToolsButton label="Histórico" onClick={() => setIsOpen(true)} />

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            px={4}
            py={2}
            color="gray.100"
            bg="#1d5840"
            borderTopLeftRadius={5}
            borderTopRightRadius={5}
            borderBottom="3px solid #ECC94B"
          >
            <Flex align="center" gap={2}>
              <MdHistory size={20} />
              <Text>Histórico de Configurações</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody bg="white" py={4}>
            {loading ? (
              <Center h={40}>
                <Flex direction="column" align="center" gap={3}>
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="green.700"
                    size="xl"
                  />
                  <Text fontSize="sm" color="gray.500">
                    Buscando dados...
                  </Text>
                </Flex>
              </Center>
            ) : (
              <>
                <Box overflowX="auto" maxH="460px" overflowY="auto">
                  <Table size="sm">
                    <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Data</Th>
                        <Th>Usuário</Th>
                        <Th>Ação</Th>
                        <Th>E-mail Antes</Th>
                        <Th>E-mail Depois</Th>
                        <Th isNumeric>Fornecedor ID</Th>
                        <Th isNumeric>Loja ID</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {items.map(item => (
                        <Tr key={item.id}>
                          <Td fontSize="12px" whiteSpace="nowrap">
                            {dayjs(item.ocorridoEm).format('DD/MM/YYYY HH:mm')}
                          </Td>
                          <Td fontSize="12px">{item.usuarioLogin}</Td>
                          <Td>{acaoBadge(item.acao)}</Td>
                          <Td
                            fontSize="12px"
                            color={item.emailAntes ? 'gray.700' : 'gray.400'}
                          >
                            {item.emailAntes ?? '—'}
                          </Td>
                          <Td
                            fontSize="12px"
                            color={item.emailDepois ? 'gray.700' : 'gray.400'}
                          >
                            {item.emailDepois ?? '—'}
                          </Td>
                          <Td isNumeric fontSize="12px">
                            {item.supplierId}
                          </Td>
                          <Td isNumeric fontSize="12px">
                            {item.storeId}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                {pagination && pagination.totalPages > 1 && (
                  <HStack justify="space-between" mt={3} px={1}>
                    <Text fontSize="12px" color="gray.500">
                      {pagination.totalRows} registros · página{' '}
                      {pagination.actualPage} de {pagination.totalPages}
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="xs"
                        isDisabled={skip === 0}
                        onClick={() => fetchData(skip - TAKE)}
                      >
                        Anterior
                      </Button>
                      <Button
                        size="xs"
                        isDisabled={
                          pagination.actualPage >= pagination.totalPages
                        }
                        onClick={() => fetchData(skip + TAKE)}
                      >
                        Próxima
                      </Button>
                    </HStack>
                  </HStack>
                )}
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ModalHistoricoConfigFollowUp
